import { useContext, useEffect, lazy } from 'react';
import { Routes, useLocation, Route } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Container, styled, Grid } from '@mui/material';
import getAppRoutes, { AppRoute } from 'App.routing';
import useHandlePWA from 'hooks/useHandlePWA';
import { useUser } from 'hooks';
import { trackVirtualPageView } from 'utils/trackVirtualPageView';
import { resetBodyPosition } from 'utils/bodyPosition';
import { IsLoggedIn } from 'utils/user';
import { Url } from 'utils/urls';
import { webSocketsConnection } from 'utils/Api/webSocketConnection';
import { PromptContext } from 'providers/PromptProvider';
import { RootDispatch, RootState } from 'storage/store';
import { Footer } from 'components/Footer';
import GlobalErrors from 'components/GlobalErrors/GlobalErrors';
import { Menu } from 'components/Menu';
import { domainConfig } from 'utils/domainConfig';
import { RouteGuard, ScrollToTop } from 'components';
import { MenuVisibilityProvider } from '../Menu/utils/menuVisibilityContext';
import { WebSocketsEvents } from '../WebSocketsEvents';
import { SigmaAvailabilityGuard } from '../SigmaAvailabilityGuard';
import useImpersonation from './hooks/useImpersonation';
import LiveChatComponent from './components/LiveChatComponent';
import StatementsGuard from '../Statements/StatementsGuard';
import { HotjarGuard } from '../HotjarGuard';
import { useContractOrAgreement } from '../../hooks/useContractOrAgreement';
import { useStatementAndContents } from '../../hooks/useStatementAndContents';

import {
  CONTRACTS_DASHBOARD,
  CONTRACT_DETAILS,
  CONTACT_DETAILS,
  CONSENTS_AND_OBLIGATIONS
} from '../../utils/featureTogglesNames';

const LastUrl = lazy(() => import('components/LastUrl'));
const Impersonation = lazy(
  () => import('components/Impersonation/Impersonation')
);
const Regulation = lazy(() => import('components/Regulation/Regulation'));

const LayoutContent = styled('div')`
  display: flex;
  flex: 1;
  overflow-x: hidden;
`;

const StyledContainer = styled(Container)`
  flex: 1;
`;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Layout = () => {
  const isTermsAndConditionsModalOpen = useSelector(
    ({ modals }: RootState) => modals.isRegulationOpen
  );
  const consentToggle = useSelector(
    (state: RootState) => state.application?.consentsAndObligationsFeatureToggle
  );
  const contractToggle = useSelector(
    (state: RootState) => state.application?.contractDashboardFeatureToggle
  );
  const { toggleLoading } = useSelector(
    (state: RootState) => state.application
  );
  const isLoggedIn = IsLoggedIn();

  useEffect(() => {
    const fetchFeatureToggle = async () => {
      dispatch.application.storeToggleLoading(true);
      try {
        await dispatch.application.getFeatureToggleState(CONTRACTS_DASHBOARD);
        await dispatch.application.getFeatureToggleState(CONTRACT_DETAILS);
        await dispatch.application.getFeatureToggleState(
          CONSENTS_AND_OBLIGATIONS
        );
        await dispatch.application.getFeatureToggleState(CONTACT_DETAILS);
        dispatch.application.storeToggleLoading(false);
      } catch (error) {
        dispatch.application.storeToggleLoading(false);
      } finally {
        dispatch.application.storeToggleLoading(false);
      }
    };
    fetchFeatureToggle();
  }, [isLoggedIn]);

  const { agreements, loadAgreementsOrContracts } = useContractOrAgreement();
  const { loadStatements } = useStatementAndContents();
  const { t } = useTranslation();
  const { impersonator, callbackurl } = useImpersonation();
  const { pathname } = useLocation();
  const dispatch = useDispatch<RootDispatch>();
  const prompt = useContext(PromptContext);
  const user = useUser();
  const userId = user.id;
  const { step } = useSelector((state: RootState) => state.processStep);
  const { sigmaAvailable } = useSelector(
    (state: RootState) => state.application
  );

  const { sharedDocumentsForUser } = useSelector(
    (state: RootState) => state.documents
  );

  const { isPl } = domainConfig;

  useHandlePWA(prompt, isLoggedIn);

  useEffect(() => {
    if (
      !agreements &&
      isLoggedIn &&
      pathname === Url.dashboard &&
      !toggleLoading
    ) {
      loadAgreementsOrContracts();
    }
  }, [isLoggedIn, agreements, pathname, toggleLoading]);

  useEffect(() => {
    if (
      document.body.style.position === 'fixed' &&
      !isTermsAndConditionsModalOpen
    ) {
      resetBodyPosition();
    }
  }, [isTermsAndConditionsModalOpen]);

  useEffect(() => {
    if (!sharedDocumentsForUser && isLoggedIn) {
      getSharedDocuments();
    }
  }, [isLoggedIn, sharedDocumentsForUser]);

  const getSharedDocuments = async () => {
    await dispatch.documents.getSharedDocumentsForUser();
  };

  const globalErrors =
    !sigmaAvailable &&
    pathname !== Url.faq &&
    pathname !== Url.unloggedContact ? null : (
      <GlobalErrors />
    );

  const routes = getAppRoutes(t).map((props: AppRoute) => (
    <Route
      key={`sigma-availability-guard-${props.key}`}
      element={<SigmaAvailabilityGuard />}
    >
      <Route
        element={
          <RouteGuard
            {...props}
            path={pathname.startsWith('//') ? pathname : props.path}
            key={`route-guard-${props.key}`}
          />
        }
        key={props.key}
      >
        <Route path={props.path} element={props.element} />
      </Route>
    </Route>
  ));

  useEffect(() => {
    if (isLoggedIn && !userId && pathname === Url.dashboard) {
      dispatch.user.fetchUserData();
    }
  }, [isLoggedIn, pathname]);

  useEffect(() => {
    if (isLoggedIn && !toggleLoading) {
      if (isPl) {
        loadStatements();
      }
      webSocketsConnection(!!contractToggle);
    }
  }, [isLoggedIn, consentToggle, toggleLoading]);

  useEffect(() => {
    if (step || pathname !== Url.register) {
      trackVirtualPageView({ pathname, step, userId });
    }
  }, [step, pathname]);

  return (
    <MenuVisibilityProvider>
      <ScrollToTop>
        <>
          <HotjarGuard />
          <StatementsGuard />
          {isLoggedIn && <LastUrl />}
          {user?.impersonation && impersonator && (
            <Impersonation
              impersonator={impersonator}
              callbackUrl={callbackurl}
            />
          )}
          <LiveChatComponent />
          {globalErrors}
          {WebSocketsEvents()}
          {isTermsAndConditionsModalOpen && <Regulation />}
          <Menu />
          <StyledContainer maxWidth="xl" disableGutters>
            <Grid
              container
              spacing={{ xs: 2, sm: 2, md: 2, lg: 2.5, xl: 2.5 }}
              columns={{ xs: 4, sm: 4, md: 8, lg: 12, xl: 12 }}
              sx={{
                flexDirection: 'column',
                flex: 1
              }}
            >
              <Grid
                sx={{
                  width: '100%',
                  display: 'flex',
                  flex: 1
                }}
              >
                <LayoutContent>
                  <SentryRoutes>{routes}</SentryRoutes>
                </LayoutContent>
              </Grid>
            </Grid>
          </StyledContainer>
          <Footer />
        </>
      </ScrollToTop>
    </MenuVisibilityProvider>
  );
};

export default Layout;
